import { demoCustomers, demoNotes } from './customers'
import { seedGuestBookings, seedLeads, seedNewsletter, seedOrders, type SeedOrder } from './inbox'
import { seedAgendaAppointments, seedAgendaBlocks } from './agenda'
import { seedSalesOrders } from './sales'
import { seedCredits, seedVouchers } from './vouchers'
import { seedStockCounts, seedStockItems, seedStockMovements, seedStockReceipts } from './stock'
import { localCatalog } from '../../data/catalog'
import { pricedLines, productOf, shippingFor } from '../pricing'
import { today } from '../../core/format/dates'
import { vatRateOf } from '../../core/rules/revenue'
import type { Account, AccountOrder, Appointment } from '../../core/types/account'
import type { InboxBooking, InboxLead, OrderFulfilment } from '../../core/types/adminRequests'
import type { AppointmentRow, OrderRow, Tables } from '../db'

/**
 * DEMO — de voorbeelddata omzetten naar de tabellen van `demo/db.ts`. Alleen gebruikt bij de
 * eerste keer laden, of als de opgeslagen stand een andere schemaversie heeft.
 */

/** Eerste demo-bestelnummer; daarna telt het door. */
export const FIRST_ORDER_NUMBER = 1001

/** Vorm van de voorbeelddata in `seed/`: één blok per klant, bestellingen zowel bij de klant als in de inbox. */
type SeedData = {
  accounts: Account[]
  notes: Record<string, string>
  orders: SeedOrder[]
  guestBookings: InboxBooking[]
  leads: InboxLead[]
  newsletter: { subscribers: number; lastWeek: number }
}

const kindFor = (treatmentSlug: string): Appointment['kind'] =>
  localCatalog.treatments.find((t) => t.slug === treatmentSlug)?.online ? 'video' : 'salon'

const fulfilmentFrom = (status: AccountOrder['status']): OrderFulfilment =>
  status === 'Bezorgd' ? 'bezorgd' : status === 'Onderweg' ? 'verzonden' : 'nieuw'

/** Klantblokken uit elkaar halen en elke bestelling en afspraak maar één keer bewaren. */
function toTables(data: SeedData): Tables {
  const customers = data.accounts.map(({ orders: _orders, appointments: _appointments, ...row }) => row)

  const appointments: AppointmentRow[] = [
    ...data.accounts.flatMap((a) =>
      a.appointments.map((x) => ({ ...x, customerId: a.customer.id, receivedAt: `${today()}T00:00` })),
    ),
    ...data.guestBookings.map<AppointmentRow>(({ id, receivedAt, status, change, booking }) => ({
      id,
      receivedAt,
      treatmentSlug: booking.treatmentSlug,
      date: booking.date,
      time: booking.time,
      kind: kindFor(booking.treatmentSlug),
      status,
      request: status === 'aanvraag' ? change : undefined,
      guest: { name: booking.name, email: booking.email, firstVisit: booking.firstVisit },
    })),
  ]

  const orders = new Map<string, OrderRow>()
  data.orders.forEach((o) => {
    const subscribe = o.order.lines.some((l) => l.subscribe)
    const lines = pricedLines(o.order.lines)
    orders.set(o.id, {
      id: o.id,
      receivedAt: o.receivedAt,
      customerId: o.customerId,
      name: o.name,
      city: o.city,
      fulfilment: o.status,
      ...(o.tracking ? { tracking: o.tracking } : {}),
      request: o.order,
      payment: o.payment,
      gift: o.gift,
      lines,
      shipping: shippingFor(lines, subscribe),
      viaSubscription: subscribe,
    })
  })
  data.accounts.forEach(({ customer, orders: accountOrders }) =>
    accountOrders.forEach((o) => {
      const known = orders.get(o.id)
      const paid = { lines: o.lines, shipping: o.shipping, total: o.total, viaSubscription: o.viaSubscription, ...(o.tracking ? { tracking: o.tracking } : {}) }
      orders.set(
        o.id,
        known
          ? { ...known, ...paid, customerId: customer.id }
          : {
              id: o.id,
              receivedAt: `${o.date}T12:00`,
              customerId: customer.id,
              name: `${customer.firstName} ${customer.lastName}`.trim(),
              city: customer.address.city,
              fulfilment: fulfilmentFrom(o.status),
              request: {
                email: customer.email,
                address: { firstName: customer.firstName, lastName: customer.lastName, ...customer.address },
                lines: o.lines.map((l) => ({ productId: l.productId, qty: l.qty, subscribe: o.viaSubscription })),
                code: null,
                skinNote: '',
                refillReminder: false,
              },
              payment: { status: 'betaald', method: customer.payment.method },
              gift: null,
              ...paid,
            },
      )
    }),
  )

  return {
    customers,
    orders: [...orders.values()],
    appointments,
    blocks: [],
    notes: { ...data.notes },
    leads: [...data.leads],
    newsletter: { base: { ...data.newsletter }, signups: [] },
    content: null,
    vouchers: [],
    credits: [],
    sales: [],
    cashDays: [],
    kuurStops: [],
    saleReturns: [],
    stockItems: seedStockItems,
    stockMovements: seedStockMovements,
    stockReceipts: seedStockReceipts,
    stockCounts: seedStockCounts,
  }
}

/** De complete demo-database uit de voorbeelddata. */
export function buildSeedTables(): Tables {
  const tables = toTables(
    structuredClone({
      accounts: demoCustomers,
      notes: demoNotes,
      orders: seedOrders,
      guestBookings: seedGuestBookings,
      leads: seedLeads,
      newsletter: seedNewsletter,
    }),
  )
  const agenda = seedAgendaAppointments()
  // Een afgeronde behandeling is bij de klant een bezoek, met het advies voor thuis.
  agenda.forEach((a) => {
    const row = a.outcome === 'afgerond' && tables.customers.find((c) => c.customer.id === a.customerId)
    if (row && !row.visits.some((v) => v.date === a.date && v.treatmentSlug === a.treatmentSlug)) {
      row.visits = [{ treatmentSlug: a.treatmentSlug, date: a.date, advice: a.products?.advice ?? [] }, ...row.visits]
    }
  })
  // De bestaande voorbeeldorders krijgen hun btw-tarief; daarna ±60 betaalde orders voor de omzet.
  const orders = [
    ...tables.orders.map((o) => ({ ...o, lines: o.lines.map((l) => ({ ...l, vatRate: l.vatRate ?? vatRateOf(productOf(l.productId) ?? {}) })) })),
    ...seedSalesOrders(tables.customers),
  ]
  // Doorlopende nummers in volgorde van betalen, zoals de webhook ze zou geven.
  const paidOrder = orders
    .filter((o) => o.payment.status === 'betaald')
    .sort((a, b) => (a.paidAt ?? a.receivedAt).localeCompare(b.paidAt ?? b.receivedAt))
    .map((o) => o.id)
  const numbered = orders.map((o) => {
    const i = paidOrder.indexOf(o.id)
    return i >= 0 ? { ...o, number: FIRST_ORDER_NUMBER + i } : o
  })
  return {
    ...tables,
    orders: numbered,
    appointments: [...tables.appointments.filter((a) => !agenda.some((x) => x.id === a.id)), ...agenda],
    blocks: seedAgendaBlocks(),
    // Elke verkochte bon uit de voorbeeldorders bestaat ook echt, plus een paar uit de salon.
    vouchers: seedVouchers(numbered),
    credits: seedCredits(tables.customers),
  }
}
